import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Car,
  Receipt,
  Wrench,
  BarChart3,
  BookOpen,
  Settings,
  LogOut,
  Zap,
  Users,
  FileText,
  Menu,
  Repeat,
  Upload,
} from "lucide-react";
import { Toaster } from "sonner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard", primary: true },
  { to: "/courses", label: "Courses", icon: Car, testid: "nav-courses", primary: true },
  { to: "/depenses", label: "Dépenses", icon: Receipt, testid: "nav-depenses", primary: true },
  { to: "/charges-fixes", label: "Charges fixes", icon: Repeat, testid: "nav-recurring" },
  { to: "/import", label: "Import CSV", icon: Upload, testid: "nav-import" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents" },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3, testid: "nav-stats", primary: true },
  { to: "/vehicule", label: "Véhicule", icon: Wrench, testid: "nav-vehicule" },
  { to: "/comptabilite", label: "Comptabilité", icon: BookOpen, testid: "nav-compta" },
  { to: "/parametres", label: "Paramètres", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex flex-col w-64 h-screen fixed left-0 top-0 border-r border-border bg-background/95 backdrop-blur-xl z-40">
        <div className="px-6 pt-8 pb-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center">
              <Zap className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="font-manrope font-extrabold text-lg leading-none">VTC Lyon</div>
              <div className="overline mt-1">Micro-entrepreneur</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 no-scrollbar">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `mx-3 mb-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-primary/15 text-primary border border-primary/25"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground border border-transparent"
                }`
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={2} />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2 text-xs text-muted-foreground truncate" data-testid="sidebar-user-email">
            {user?.username}
          </div>
          <button
            onClick={handleLogout}
            data-testid="btn-logout"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Top header (mobile) */}
      <header className="lg:hidden sticky top-0 w-full backdrop-blur-xl bg-background/80 border-b border-border z-30 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <button className="p-1.5 rounded-md hover:bg-accent" data-testid="btn-mobile-menu">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-background border-border">
              <div className="px-6 pt-8 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-md bg-primary/15 border border-primary/30 flex items-center justify-center">
                    <Zap className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="font-manrope font-extrabold">VTC Lyon</div>
                    <div className="overline mt-1">Micro-entrepreneur</div>
                  </div>
                </div>
              </div>
              <nav className="py-3">
                {NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/"}
                    data-testid={`${item.testid}-drawer`}
                    className={({ isActive }) =>
                      `mx-3 mb-1 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                        isActive
                          ? "bg-primary/15 text-primary border border-primary/25"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground border border-transparent"
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
          <div className="h-8 w-8 rounded-md bg-primary/15 border border-primary/30 flex items-center justify-center">
            <Zap className="h-4 w-4 text-primary" />
          </div>
          <div className="font-manrope font-extrabold">VTC Lyon</div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="btn-logout-mobile"
          className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1.5"
        >
          <LogOut className="h-4 w-4" />
          Sortir
        </button>
      </header>

      {/* Content */}
      <main className="lg:pl-64 pb-24 lg:pb-8 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="fixed bottom-0 w-full backdrop-blur-xl bg-background/85 border-t border-border z-50 lg:hidden">
        <div className="grid grid-cols-5">
          {NAV.filter((n) => n.primary).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`${item.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center py-3 gap-1 text-[10px] transition-colors ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <item.icon className="h-5 w-5" strokeWidth={2} />
              <span className="font-semibold">{item.label.split(" ")[0]}</span>
            </NavLink>
          ))}
          <NavLink
            to="/parametres"
            data-testid="nav-settings-mobile"
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-3 gap-1 text-[10px] transition-colors ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <Settings className="h-5 w-5" strokeWidth={2} />
            <span className="font-semibold">Réglages</span>
          </NavLink>
        </div>
      </nav>

      <Toaster position="top-right" theme="dark" richColors closeButton />
    </div>
  );
}
